<template>
  <v-app-bar
    :flat="true"
    :app="false"
    class="tw:overflow-visible tw:border tw:border-l-0 tw:border-r-0 tw:border-t-primary tw:bg-primary tw:fixed tw:w-full tw:z-1000"
    id="header-root"
    ref="headerRef">
    <router-link id="logo" to="/">
      <v-app-bar-title class="tw:p-4">
        <img :src="logo" class="tw:top-1 tw:h-15" alt="CAC - Part of The Baldwin Group" />
      </v-app-bar-title>
    </router-link>

    <v-spacer />

    <NavMenuItem open-on-hover title="Clients" :links="clientLinks" v-if="isLoggedIn && hasRole" showBorder />
    <NavMenuItem open-on-hover title="Book of Business" :links="bookOfBusinessLinks" v-if="isLoggedIn && hasRole" showBorder />
    <NavMenuItem open-on-hover title="Marketplace" :links="marketplaceLinks" v-if="isLoggedIn && hasRole" showBorder />
    <NavMenuItem open-on-hover title="Management" :links="managementLinks" v-if="isLoggedIn && hasRole" showBorder />
    <NavMenuItem open-on-hover title="Admin" :links="adminLinks" v-if="isLoggedIn && isAdmin && hasRole" showBorder />
    <UserFeedbackModal :show="showUserFeedbackModal" @close="showUserFeedbackModal = false" />
    <AppButton
      v-if="!isLoggedIn"
      class="tw:btn-ghost tw:btn-sm tw:text-white tw:hover:bg-white/20 tw:border-0"
      @click="signin"
    >
      <CircleUser :size="18" class="tw:mr-2 tw:stroke-white" />
      Log In
    </AppButton>
    <Notifications v-if="canViewNotifications" />
    <AppMenu v-if="isLoggedIn" @handle-option="handleMenuOption" />
  </v-app-bar>
</template>

<script lang="ts">
import Store, { Commit } from '@/store';
import {
  GetAllowedAccountIds,
  GetAllowedCarrierIds,
  GetAllowedWholesalerIds,
  IsMarketUser,
  IsUserABroker,
  IsUserAClient,
  IsUserAnAdmin,
  UserHasPermission
} from '@/utilities/AuthUtils';
import { Auth } from 'aws-amplify';
import NavMenuItem from '@/components/NavMenuItem.vue';
import UserFeedbackModal from './UserFeedbackModal.vue';
import { UserPermission } from '@/types/UserPermissions';
import { StoreMutationType } from '@/types/StoreMutationType';
import logo from '@/assets/images/cac_logo_part_of_TBG_white.svg';
import Notifications from '@/components/Notifications.vue';
import AppMenu from './AppMenu.vue';
import AppButton from '@/components/v2/AppButton.vue';
import { registerResizeItem } from '@/utilities/resize';
import { useHeaderOffset } from '@/composables/useHeaderOffset';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import {
  Boxes,
  Building,
  BrickWall,
  ChartColumn,
  ChartColumnStacked,
  ChartNoAxesCombined,
  ChartLine,
  CircleUser,
  ClipboardList,
  CloudCog,
  Database,
  Factory,
  FileCheck,
  KeyRound,
  NotebookText,
  Loader,
  Palette,
  Percent,
  Search,
  SquarePen,
  Star,
  Table,
} from 'lucide-vue-next';

export default {
  name: 'AppHeader',
  components: { AppButton, Notifications, NavMenuItem, UserFeedbackModal, AppMenu, CircleUser },
  setup() {
    const headerRef = ref<HTMLElement | null>(null);
    const { updateHeaderHeight } = useHeaderOffset();
    
    const updateHeight = async () => {
      await nextTick();
      // Get the actual DOM element from the v-app-bar component
      const headerElement = document.getElementById('header-root');
      if (headerElement) {
        const height = headerElement.clientHeight;
        updateHeaderHeight(height);
      }
    };
    
    onMounted(() => {
      updateHeight();
      window.addEventListener('resize', updateHeight);
    });
    
    onUnmounted(() => {
      window.removeEventListener('resize', updateHeight);
    });
    
    return { headerRef };
  },
  data() {
    return {
      logo: logo,
      showUserFeedbackModal: false,
    };
  },
  computed: {
    clientLinks() {
      return [
        {
          title: 'Client Management',
          icon: Building,
          to: IsUserAClient(Store.state.user) ? '/clients' : '/accounts',
          hidden:
            IsMarketUser(Store.state.user) ||
            (IsUserAClient(Store.state.user) && this.userAllowedAccounts.length <= 1),
        },
        {
          title: 'Analytics Hub',
          to: '/analytics',
          icon: ChartColumn,
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
        {
          title: 'Towers',
          to: '/v2/tower',
          icon: ChartColumnStacked,
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
        {
          title: 'AI Data Extraction Results',
          to: '/ai-results',
          icon: FileCheck,
          hidden:
            IsUserAClient(Store.state.user) ||
            IsMarketUser(Store.state.user) ||
            (IsUserABroker(Store.state.user) && !UserHasPermission(Store.state.user, UserPermission.AIVerificationWrite)),
        },
      ];
    },
    bookOfBusinessLinks() {
      return [
        {
          title: 'Policy Explorer',
          icon: Search,
          to: '/policy/explorer',
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
      ];
    },
    marketplaceLinks() {
      return [
        {
          title: 'Carrier Analytics',
          icon: Table,
          to: '/carriers',
          hidden:
            IsUserAClient(Store.state.user) ||
            (IsMarketUser(Store.state.user) && GetAllowedCarrierIds(Store.state.user).length === 0),
        },
        {
          title: 'Wholesaler Analytics',
          icon: BrickWall,
          to: '/wholesaler-group',
          hidden:
            IsUserAClient(Store.state.user) ||
            (IsMarketUser(Store.state.user) && GetAllowedWholesalerIds(Store.state.user).length === 0),
        },
        {
          title: 'Carrier Survey Results',
          icon: ClipboardList,
          to: '/carriers/surveys',
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
        {
          title: 'AM Best Ratings',
          icon: Star,
          to: '/am-best',
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
      ];
    },
    managementLinks() {
      return [
        {
          title: 'Management Reporting',
          to: '/management-analytics',
          icon: ChartLine,
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
        {
          title: 'Reporting Hub',
          to: '/reporting',
          icon: Factory,
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
        {
          title: 'Revenue Dashboard',
          to: '/revenue-dashboard',
          icon: ChartNoAxesCombined,
          hidden: !IsUserAnAdmin(Store.state.user),
        },
        {
          title: 'Yield Analysis',
          to: '/yield-analysis',
          icon: Percent,
          hidden: !IsUserAnAdmin(Store.state.user),
        },
      ];
    },
    adminLinks() {
      const links = [
        { title: 'Carrier Group Mapping', to: '/admin/carrier-issuer', icon: NotebookText },
        { title: 'Policy Type Mapping', to: '/admin/policy-type', icon: SquarePen },
        { title: 'Wholesaler Mapping', to: '/admin/wholesaler', icon: Boxes },
        { title: 'Data Quality', to: '/admin/data-quality', icon: Database },
        { title: 'Cache Management', to: '/admin/cache', icon: CloudCog },
        { title: 'Permissions Management', to: '/admin/permissions', icon: KeyRound },
        { title: 'AI Data Extraction', to: '/admin/ai', icon: Loader },
        { title: 'Reporting', to: '/admin/reporting', icon: ChartColumn },
      ];

      // Only add Component Showcase in development
      if (import.meta.env.MODE === 'development') {
        links.push({ title: 'Custom Components', to: '/admin/component-showcase', icon: Palette });
      }

      return links;
    },
    isLoggedIn: function () {
      return !!Store.state.user;
    },
    isAdmin: function () {
      return IsUserAnAdmin(Store.state.user);
    },
    canViewNotifications: function () {
      return this.isAdmin || UserHasPermission(Store.state.user, UserPermission.NotificationsRead);
    },
    userAllowedAccounts: function () {
      return GetAllowedAccountIds(Store.state.user);
    },
    hasRole: function () {
      // user has at least 1 role
      return (Store.state.user?.roles || []).length > 0;
    },
  },
  methods: {
    async signin() {
      await Auth.federatedSignIn();
    },
    handleMenuOption(option: string) {
      switch (option) {
        case 'feedback':
          this.showUserFeedbackModal = true;
          break;
        case 'client-views':
          Commit(StoreMutationType.SET_SHOW_CLIENT_VIEWS_MODAL, true);
          break;
        case 'notifications':
          this.$router.push('/notifications');
          break;
      }
    },
  },
  mounted() {
    registerResizeItem({ name: 'header', selector: 'header-root' });
  },
};
</script>
